import {useQuery} from '@apollo/client';
import {DatePicker, Form, Spin} from 'antd';
import {
  UserOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  DollarOutlined,
  CodeSandboxOutlined,
} from '@ant-design/icons';
import {Bar} from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import {
  DABA_PRO_SUBSCRIPTION_STATISTICS,
  GET_ASSET_CLASS_AND_DABAPRO_DATA,
  DABA_PRO_SUBSCRIPTION_MONTHLY_BREAKDOWN,
} from '../resolvers';
import styles from '../analytics.module.scss';
import {useMemo, useState} from 'react';
import SubmitButton from 'components/Startup/components/SubmitButton';
import moment from 'moment';
import {DabaProMembersTable} from './DabaProMembersTable';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export const DabaProOverviewPage = () => {
  const [form] = Form.useForm();
  const [selectedYear, setSelectedYear] = useState(moment());
  const [appliedFilters, setAppliedFilters] = useState(null);

  const {
    data,
    loading,
    refetch: refetchStats,
  } = useQuery(DABA_PRO_SUBSCRIPTION_STATISTICS, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: chartData,
    loading: chartLoading,
    refetch: refetchChart,
  } = useQuery(GET_ASSET_CLASS_AND_DABAPRO_DATA, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const {data: monthlyData, loading: monthlyLoading} = useQuery(
    DABA_PRO_SUBSCRIPTION_MONTHLY_BREAKDOWN,
    {
      variables: {year: Number(selectedYear.format('YYYY'))},
      fetchPolicy: 'no-cache',
    },
  );

  const onFinish = values => {
    const {startDate, endDate} = values || {};
    const filters = {
      startDate: startDate ? moment(startDate).format() : null,
      endDate: endDate ? moment(endDate).format() : null,
    };
    setAppliedFilters(filters);
    refetchStats({filters});
    refetchChart({filters});
  };

  const stats = useMemo(() => {
    return data?.getDabaProSubscriptionStatistics?.data?.__typename ===
      'DabaProSubscriptionStatisticsData'
      ? data.getDabaProSubscriptionStatistics.data
      : {};
  }, [data]);

  const cardData = useMemo(
    () => [
      {
        title: 'Total Revenue',
        value: `${stats.totalRevenue?.symbol || '$'}${Number(
          stats.totalRevenue?.totalAmount || 0,
        ).toLocaleString()}`,
        subtitle: stats.totalRevenue?.currency || 'USD',
        icon: <DollarOutlined style={{fontSize: '24px', color: '#13c2c2'}} />,
        iconBg: '#e6fffb',
        isValueString: true,
      },
      {
        title: 'Total Units Sold',
        value: stats.totalSoldUnits || 0,
        subtitle: 'Specified period',
        icon: (
          <CodeSandboxOutlined style={{fontSize: '24px', color: '#722ed1'}} />
        ),
        iconBg: '#f9f0ff',
      },
      {
        title: 'Total Daba Pro Members',
        value: stats.totalCount || 0,
        subtitle: 'Since launch',
        icon: <UserOutlined style={{fontSize: '24px', color: '#1890ff'}} />,
        iconBg: '#e6f7ff',
      },
      {
        title: 'Active Members',
        value: stats.totalActive || 0,
        subtitle: 'Currently active',
        icon: <UserAddOutlined style={{fontSize: '24px', color: '#52c41a'}} />,
        iconBg: '#f6ffed',
      },
      {
        title: 'Inactive Members',
        value: stats.totalInactive || 0,
        subtitle: 'Expired subscriptions',
        icon: (
          <UserDeleteOutlined style={{fontSize: '24px', color: '#8c8c8c'}} />
        ),
        iconBg: '#f5f5f5',
      },
    ],
    [stats],
  );

  const dabaProSubscriptionArray =
    chartData?.getAssetClassAndDabaProChartData?.data?.dabaPro || [];

  const dabaProSubscriptionData = {
    labels: dabaProSubscriptionArray.map(item =>
      item?.label?.replaceAll('_', ' '),
    ),
    datasets: [
      {
        label: 'Daba Pro Subscription',
        data: dabaProSubscriptionArray.map(item => Number(item?.value) || 0),
        backgroundColor: ['#7C3AED', '#06B6D4', '#22C55E'],
      },
    ],
  };

  const dabaProOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {legend: {position: 'top'}},
  };

  const monthlyBreakdownData = useMemo(() => {
    const breakdown =
      monthlyData?.getDabaProSubscriptionMonthlyBreakdown?.data?.breakdown ||
      [];
    const breakdownMap = breakdown.reduce((acc, item) => {
      if (item.month) {
        acc[item.month.toLowerCase()] = item.totalCount;
      }
      return acc;
    }, {});

    return moment.months().map(month => ({
      month,
      count: breakdownMap[month.toLowerCase()] || 0,
    }));
  }, [monthlyData]);

  if ((!data && loading) || (!chartData && chartLoading)) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.filterSection}>
        <Form layout="inline" onFinish={onFinish} form={form}>
          <Form.Item label="Start Date" name="startDate">
            <DatePicker showTime />
          </Form.Item>
          <Form.Item label="End Date" name="endDate">
            <DatePicker showTime />
          </Form.Item>
          <SubmitButton label="Apply" smallWidth />
        </Form>
      </div>
      <div
        style={{
          marginBottom: '16px',
          fontSize: '14px',
          color: '#595959',
          padding: '12px 16px',
          backgroundColor: '#e6f7ff',
          borderRadius: '8px',
          border: '1px solid #91d5ff',
        }}>
        <strong>Note:</strong> Date filters only apply to{' '}
        <strong>Total Revenue</strong>, <strong>Total Units Sold</strong> and
        the <strong>Chart</strong>.<br />
        {appliedFilters?.startDate || appliedFilters?.endDate ? (
          <span>
            Currently showing filtered results from{' '}
            <strong>
              {appliedFilters?.startDate
                ? moment(appliedFilters.startDate).format('MMMM Do, YYYY')
                : moment().startOf('month').format('MMMM Do, YYYY')}
            </strong>{' '}
            to{' '}
            <strong>
              {appliedFilters?.endDate
                ? moment(appliedFilters.endDate).format('MMMM Do, YYYY')
                : moment().format('MMMM Do, YYYY')}
            </strong>
            .
          </span>
        ) : (
          <span>
            By default, showing results from{' '}
            <strong>{moment().startOf('month').format('MMMM Do, YYYY')}</strong>{' '}
            to <strong>{moment().format('MMMM Do, YYYY')}</strong>.
          </span>
        )}
      </div>
      <div className={styles.dabaProGrid}>
        {cardData.map((item, index) => {
          const isAffectedByDate = index <= 1;
          const isCardLoading = isAffectedByDate && loading;

          return (
            <Spin spinning={isCardLoading} key={index}>
              <div
                className={`${styles.metricOverviewCard} ${
                  isAffectedByDate ? styles.filteredMetricCard : ''
                }`}>
                <div
                  className={styles.dabaProIconWrapper}
                  style={{backgroundColor: item.iconBg}}>
                  {item.icon}
                </div>
                <div className={styles.dabaProTextWrapper}>
                  <span className={styles.dabaProValue}>
                    {item.isValueString
                      ? item.value
                      : Number(item.value).toLocaleString()}
                  </span>
                  <span className={styles.dabaProTitle}>{item.title}</span>
                  <span className={styles.dabaProSubtitle}>
                    {item.subtitle}
                  </span>
                </div>
              </div>
            </Spin>
          );
        })}
      </div>
      <div className={styles.chartSection}>
        <Spin spinning={chartLoading}>
          <div className={styles.chartContainer}>
            <Bar data={dabaProSubscriptionData} options={dabaProOptions} />
          </div>
        </Spin>
      </div>
      <div className={styles.breakdownSection}>
        <div className={styles.breakdownHeader}>
          <h3 className={styles.sectionTitle}>
            {selectedYear.format('YYYY')} MONTHLY SALES BREAKDOWN
          </h3>
          <div className={styles.yearFilter}>
            <span className={styles.filterLabel}>Filter Breakdown by Year</span>
            <DatePicker
              picker="year"
              allowClear={false}
              value={selectedYear}
              onChange={val => val && setSelectedYear(val)}
              className={styles.yearPicker}
            />
          </div>
        </div>
        {monthlyLoading ? (
          <div className={styles.loading} style={{height: '200px'}}>
            <Spin />
          </div>
        ) : (
          <div className={styles.monthlyBreakdownGrid}>
            {monthlyBreakdownData.map((item, index) => (
              <div key={index} className={styles.monthlyCard}>
                <span className={styles.monthLabel}>{item.month}</span>
                <span className={styles.monthValue}>{item.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <DabaProMembersTable />
    </div>
  );
};
